import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Empty, Pagination, Spin } from "antd";
import axios from "axios";
import { HeaderImage, SearchBar } from "@bindeleddet/core/components";
import { Company } from "@bindeleddet/core/types";
import { useViewport } from "@bindeleddet/core/hooks";
import CompaniesImage from "assets/images/headerImage/CompaniesImage.png";
import CompaniesFilter from "./CompaniesFilter/CompaniesFilter";
import CompanyCard from "./CompanyCard/CompanyCard";

const Companies = () => {
  const { width } = useViewport();
  const isMobile = width < 768;
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sectorsChecked, setSectorsChecked] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(24);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Fetch companies from backend with pagination, search and sector filters
  const fetchCompanies = useCallback(async (page: number, size: number, search: string, sectors: number[]) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: size.toString(),
      });

      if (search) {
        params.append("search", search);
      }

      // Add sector filters
      sectors.forEach(sectorId => {
        params.append("sectors", sectorId.toString());
      });

      const response = await axios.get<{ results: Company[]; count: number }>(`/companies/?${params.toString()}`);

      setCompanies(response.data.results);
      setTotalCount(response.data.count);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies(currentPage, pageSize, searchTerm, sectorsChecked);
  }, [currentPage, pageSize, searchTerm, sectorsChecked, fetchCompanies]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Functions to toggle filter and search term states
  const handleSearch = (newSearchTerm: React.SetStateAction<string>) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  const toggleCheckedSectors = useCallback((id: number) => {
    setSectorsChecked(previousState => {
      const newState = previousState.includes(id) ? previousState.filter(x => x !== id) : [...previousState, id];
      // Reset to first page when filter changes
      setCurrentPage(1);
      return newState;
    });
  }, []);

  // Rendering of components
  return (
    <>
      <HeaderImage imageLink={CompaniesImage} alt="CompaniesImage" header="Bedrifter" />
      <Row justify="center" style={{ paddingLeft: "5%", paddingRight: "5%", marginTop: "40px", marginBottom: "40px" }}>
        <Col xs={24} sm={24} md={19} lg={19}>
          <SearchBar value={searchTerm} handleSearch={handleSearch} placeholder="Søk..." />
        </Col>
      </Row>
      <Row style={{ paddingLeft: "5%", paddingRight: "5%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Col
          xs={24}
          sm={24}
          md={6}
          lg={6}
          style={{
            paddingRight: isMobile ? 0 : "8px",
            marginBottom: isMobile ? "16px" : 0,
          }}
        >
          <CompaniesFilter
            sectorsChecked={sectorsChecked}
            toggleCheckedSectors={toggleCheckedSectors}
            onFilterReset={() => {
              setSectorsChecked([]);
              handleSearch("");
            }}
          />
        </Col>
        <Col
          xs={24}
          sm={24}
          md={18}
          lg={18}
          style={{
            paddingLeft: isMobile ? 0 : "8px",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {loading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
            </div>
          ) : companies.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                searchTerm !== "" || sectorsChecked.length > 0
                  ? "Ingen bedrifter matcher søket"
                  : "Ingen bedrifter er tilgjengelige for øyeblikket"
              }
            />
          ) : (
            <>
              <Row
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  marginLeft: isMobile ? 0 : "-8px",
                  marginRight: isMobile ? 0 : "-8px",
                }}
              >
                {companies.map(company => (
                  <Col
                    key={company.id}
                    xs={12}
                    sm={12}
                    md={12}
                    lg={8}
                    xl={6}
                    style={{
                      display: "flex",
                      paddingLeft: "8px",
                      paddingRight: "8px",
                      marginBottom: "16px",
                    }}
                  >
                    <CompanyCard company={company} />
                  </Col>
                ))}
              </Row>
              <div style={{ marginTop: "24px", textAlign: "center" }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalCount}
                  onChange={handlePageChange}
                  showTotal={(total, range) => `${range[0]}-${range[1]} av ${total} bedrifter`}
                />
              </div>
            </>
          )}
          <br />
        </Col>
      </Row>
    </>
  );
};

export default Companies;
